import { FC, useEffect } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { Input, Label, Button, Password, Checkbox, Radio } from "../../common";
import { FormErrorMessage } from "../../messages";
import { TERMS_LINKS, ACCOUNT_TYPE_ROUTE_VALUES } from "../../../constants";
import { useGetUTMParams } from "../../../hooks/useGetUTMParams";
import { ROUTES } from "../../../routes";
import { selectContactLink } from "../../../store/reducers/contacts";
import { useSignupMutation } from "../../../store/services/auth";
import { AccountType } from "../../../types/UserTypes";
import { track } from "../../../utils/tracking";

type Inputs = {
  account_type: AccountType;
  company_name: string;
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  marketing_notifications: boolean;
  contact_link_id?: string;
};

export const SignupForm: FC = () => {
  const contactLink = useSelector(selectContactLink);
  const getUTMParams = useGetUTMParams();
  const navigate = useNavigate();

  const { account_type } = useParams<{
    account_type: keyof typeof ACCOUNT_TYPE_ROUTE_VALUES;
  }>();

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
    watch,
  } = useForm<Inputs>({
    defaultValues: {
      account_type: ACCOUNT_TYPE_ROUTE_VALUES[account_type || "individual"],
      company_name: "",
      first_name: contactLink?.first_name || "",
      last_name: contactLink?.last_name || "",
      email: contactLink?.email || "",
      password: "",
      marketing_notifications: false,
      contact_link_id: contactLink?.id,
    },
  });

  const [signup, { isSuccess, isLoading, isError, error }] =
    useSignupMutation();

  const isBusinessAccount =
    watch("account_type").toString() === AccountType.BUSINESS.toString();

  useEffect(() => {
    if (isSuccess) {
      if (contactLink) {
        navigate(ROUTES.AUTH_LOGIN);
      } else {
        navigate(ROUTES.AUTH_CONFIRM);
      }

      track("frontend_signup_completed");
    }
  }, [isLoading, isSuccess, navigate, contactLink]);

  const onSubmit: SubmitHandler<Inputs> = async (data) =>
    await signup({ body: data, query: getUTMParams() });

  return (
    <div className="mt8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pb-16">
          <Controller
            name="account_type"
            control={control}
            rules={{ required: true }}
            defaultValue={AccountType.CONTRACTOR}
            render={({ field, field: { value } }) => (
              <Radio
                options={[
                  { label: "Individual", value: AccountType.CONTRACTOR },
                  { label: "Company", value: AccountType.BUSINESS },
                ]}
                {...field}
                checked={value}
              />
            )}
          />
        </div>

        {isBusinessAccount && (
          <>
            <Label label="Registered business name" className="mb-8">
              <Controller
                name="company_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => <Input type="text" {...field} />}
              />
            </Label>

            <div className="pt-9 pb-6 uppercase text-sm font-manrope font-semibold text-gray-300 tracking-[1px]">
              Representative
            </div>
          </>
        )}

        <Label label="First name" className="mb-8">
          <Controller
            name="first_name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Input type="text" {...field} />}
          />
        </Label>

        <Label label="Last name" className="mb-8">
          <Controller
            name="last_name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Input type="text" {...field} />}
          />
        </Label>

        <Label label="Email" className="mb-8">
          <Controller
            name="email"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Input type="email" {...field} />}
          />
        </Label>

        <Label label="Create password" className="mb-12">
          <Controller
            name="password"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Password {...field} />}
          />
        </Label>

        <div className="mb-12">
          <Controller
            name="marketing_notifications"
            control={control}
            render={({ field, field: { value } }) => (
              <Checkbox {...field} checked={value}>
                Keep me in the loop with only the juiciest product updates
              </Checkbox>
            )}
          />
        </div>

        <FormErrorMessage className="mb-6" isError={isError} error={error} />

        <Button
          type="submit"
          variant={Button.variant.PRIMARY}
          className="w-full"
          disabled={!isDirty || !isValid}
          data-test="signup-btn"
        >
          Sign Up
        </Button>

        <div className="text-sm	text-gray-300 text-center mt-6">
          By registering, you accept our&nbsp;
          <a
            href={TERMS_LINKS.terms}
            className="theme-link"
            target="_blank"
            rel="noreferrer"
            data-test="tos-link"
          >
            Terms of use
          </a>{" "}
          and&nbsp;
          <a
            href={TERMS_LINKS.privacy}
            className="theme-link"
            target="_blank"
            rel="noreferrer"
            data-test="privacy-policy-link"
          >
            Privacy Policy
          </a>
        </div>
      </form>
    </div>
  );
};
